import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiEdit3 } from 'react-icons/fi';
import { useAskVantage } from '../../context/AskVantageProvider';
import { useTheme } from '../../theme';
import { assistantApi } from '../../services/assistant';
import type { SampleQuestion } from '../../types/api';

const PromptSuggestions: React.FC = () => {
  const { send, fillMessageBox } = useAskVantage();
  const theme = useTheme();

  const [questions, setQuestions] = useState<SampleQuestion[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch 5 random sample questions
  const fetchSampleQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await assistantApi.getSampleQuestions({
        sample: 5
      });
      setQuestions(response.questions);
    } catch (err) {
      console.error('Failed to fetch sample questions:', err);
      setError('Failed to load sample questions');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Load questions on component mount
  useEffect(() => {
    fetchSampleQuestions();
  }, []);

  const handleSuggestionClick = async (suggestion: string) => {
    try {
      await send(suggestion);
    } catch (err) {
      console.error('Failed to send suggestion:', err);
    }
  };

  const handleFillInput = (suggestion: string) => {
    if (fillMessageBox) {
      fillMessageBox(suggestion);
    }
  };

  if (error) {
    return (
      <Wrapper>
        <ErrorMessage $theme={theme}>{error}</ErrorMessage>
      </Wrapper>
    );
  }

  if (questions.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <SuggestedHeading $theme={theme}>Suggested:</SuggestedHeading>
      {loading ? (
        <LoadingContainer $theme={theme}>
          <LoadingText $theme={theme}>Loading questions...</LoadingText>
        </LoadingContainer>
      ) : (
        <SuggestionsList $theme={theme}>
          {questions.map((question) => (
            <SuggestionItem key={question.id} $theme={theme}>
              <SuggestionContent>
                <SuggestionPill
                  onClick={() => handleSuggestionClick(question.question)}
                  $theme={theme}
                >
                  {question.question}
                </SuggestionPill>
                <PencilIcon
                  className="pencil-icon"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleFillInput(question.question);
                  }}
                  $theme={theme}
                  title="Fill input with this suggestion"
                >
                  <FiEdit3 size={14} />
                </PencilIcon>
              </SuggestionContent>
            </SuggestionItem>
          ))}
        </SuggestionsList>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
`;

const SuggestedHeading = styled.h3<{ $theme: any }>`
  color: ${(props) => props.$theme.colors.quickPromptText || '#333'};
  font-size: 0.9rem;
  font-weight: 500;
  margin: 0 0 12px 0;
  padding: 0;

  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin: 0 0 10px 0;
  }

  @media (max-width: 375px) {
    font-size: 0.8rem;
    margin: 0 0 8px 0;
  }
`;

const LoadingContainer = styled.div<{ $theme: any }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
`;

const LoadingText = styled.div<{ $theme: any }>`
  color: ${(props) => props.$theme.colors.quickPromptText};
  font-size: 0.9rem;
  opacity: 0.7;
`;

const ErrorMessage = styled.div<{ $theme: any }>`
  color: ${(props) => props.$theme.colors.error || '#ef4444'};
  text-align: center;
  padding: 20px;
  font-size: 0.9rem;
`;

const SuggestionsList = styled.div<{ $theme: any }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const SuggestionItem = styled.div<{ $theme: any }>`
  width: 100%;

  &:hover .pencil-icon {
    opacity: 1;
    visibility: visible;
  }
`;

const SuggestionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const SuggestionPill = styled.button<{ $theme: any }>`
  background: transparent;
  border: none;
  color: #2b7bc5;
  padding: 5px 0;
  cursor: pointer;
  font-size: 0.875rem;
  text-align: left;
  transition: all 0.2s ease;
  line-height: 1.4;
  flex: 1;

  &:hover {
    color: #236ba3;
    text-decoration: underline;
  }

  &:active {
    color: #1f5a8a;
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 6px 0;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 5px 0;
    line-height: 1.3;
  }

  @media (max-width: 375px) {
    font-size: 0.7rem;
    padding: 4px 0;
  }
`;

const PencilIcon = styled.button<{ $theme: any }>`
  background: ${(props) => props.$theme.colors.messageBoxBg || '#f8f9fa'};
  border: 1px solid ${(props) => props.$theme.colors.quickPromptBorder || '#e9ecef'};
  border-radius: 4px;
  padding: 6px;
  cursor: pointer;
  color: #2b7bc5;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: ${(props) => props.$theme.colors.quickPromptBg || '#ffffff'};
    color: #236ba3;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    padding: 5px;
  }
`;

export default PromptSuggestions;
